import { Report } from 'shared/components/WelcomeBack/utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  FeedbackQuestion,
  FeedbackQuestionsById,
} from '../../../NPOFeedback/MakeEdits/api';
import { FeedbackSectionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import {
  SectionIndicators,
  orderedIndicatorSectionList,
} from '../IndicatorDetails/utils';
import { replaceQuestionText } from 'shared/utils/formatText';

export interface ReportQuestion {
  autoSave: boolean | null;
  dbId: number;
  enumResponses: {
    dbId: number;
    nextQuestionId: number | null;
    order: number;
    questionId: number;
    response: string;
    responseHelp: null;
    responseType: null;
    responseValue: null;
    treeGroup: null;
  }[];
  helpContent: string;
  nextQuestionId: number | null;
  questionType: FeedbackQuestion['questionType'];
  section: FeedbackSectionId;
  sectionStart: boolean | null;
  tab: null;
  text: string;
  validations: {
    dbId: number;
    text: string;
    validation: string; // 'no_input_text'
    validationType: 'error' | 'warning';
  }[];
}

export const isReportFinalized = (stage: Report['stage']): boolean =>
  stage?.toUpperCase() === 'FINAL';

export const isReportInterim = (stage: Report['stage']): boolean =>
  stage?.toUpperCase() === 'INTERIM';

export const getFinalizeMarkerQuestionId = (
  optionId: number,
  stage: Report['stage'],
): number | null => {
  const interim = isReportInterim(stage);
  switch (optionId) {
    case 1:
      return interim ? 120001 : 90001;
    case 2:
      return interim ? 120002 : 90002;
    case 3:
      return interim ? 120003 : 90003;
    case 5:
      return interim ? 120005 : 90005;
    default:
      return null;
  }
};

export const getQuestionsFromAnswersArray = (
  questions: ReportQuestion[],
  answers: Answer[],
  report?: Report,
) => {
  const questionsById: { [id: number]: ReportQuestion | undefined } = {};
  for (const q of questions) {
    questionsById[q.dbId] = q;
  }

  const result: FeedbackQuestionsById = {};

  // Seed the map with the base question definitions so that questions which have
  // not been answered yet (e.g. the Dates reporting-period question) can still
  // be opened for editing in the MakeEditsModal, instead of being treated as
  // missing and rendering the "All Done!" empty state. Answers below overlay
  // these defaults.
  for (const q of questions) {
    const questionText = replaceQuestionText({
      text: q.text || '',
      programName: report?.programName,
      year: report?.year,
    });
    result[q.dbId] = {
      answers: [],
      dbId: q.dbId,
      questionType: q.questionType,
      enumResponses: q.enumResponses || [],
      text: questionText,
    };
  }

  for (const a of answers) {
    const currentQuestion = questionsById[a.questionId];
    const questionRaw = a.questionText || currentQuestion?.text || '';
    const questionText = replaceQuestionText({
      text: questionRaw,
      programName: report?.programName,
      year: report?.year,
    });

    result[a.questionId] = {
      answers: [
        {
          date1: a.date1,
          date2: a.date2,
          dbId: a.dbId,
          text: a.text,
          questionText,
        },
      ],
      dbId: a.questionId,
      questionType: currentQuestion?.questionType || a.questionType,
      enumResponses: currentQuestion?.enumResponses || [],
      text: a.text,
    };
  }
  return result;
};

export const finalForecastValidation = (
  indicators: SectionIndicators,
): boolean => {
  for (const sectionKey of orderedIndicatorSectionList) {
    for (const x of indicators[sectionKey]) {
      if (x.finalForecast === undefined || x.finalForecast === '') {
        return false;
      }
    }
  }
  return true;
};

export const autofillFinalForecast = (
  indicators: SectionIndicators,
): SectionIndicators => {
  for (const sectionKey of orderedIndicatorSectionList) {
    for (const x of indicators[sectionKey]) {
      if (x.finalForecast === undefined || x.finalForecast === '') {
        x.finalForecast = x.forecast;
      }
    }
  }
  return indicators;
};

interface UpdateAnswerState {
  questionId: number;
  answer: string;
  questionText: string;
  questionType: FeedbackQuestion['questionType'];
  questions: FeedbackQuestionsById | undefined;
}

export const updateAnswerState = ({
  questionId,
  answer,
  questions,
  questionText,
  questionType,
}: UpdateAnswerState) => {
  if (!questions) return questions;
  const newState = { ...questions };
  if (!newState[questionId]) {
    newState[questionId] = {
      dbId: questionId,
      enumResponses: [],
      questionType,
      text: questionText,
      answers: [
        {
          dbId: questionId,
          date1: null,
          date2: null,
          questionText: questionText,
          text: answer,
        },
      ],
    };
  } else if (newState[questionId]!.answers.length === 0) {
    newState[questionId]!.answers = [
      {
        dbId: questionId,
        date1: null,
        date2: null,
        questionText,
        text: answer,
      },
    ];
  } else {
    newState[questionId]!.answers[0]!.text = answer;
  }
  return newState;
};
